import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { canOpenPortfolio } from '@/lib/constants/dashboard';
import { useTheme } from '@/context/ThemeContext';
import { useAccountSettings } from '@/context/AccountSettingsContext';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { SheetClose } from '@/components/ui/sheet';
import { cn } from '@/lib/core/utils';
import {
  X,
  Sun,
  Moon,
  Monitor,
  LayoutDashboard,
  Users,
  Building2,
  Settings,
  LogOut,
  ChevronRight,
} from 'lucide-react';

interface MobileMenuPageContentProps {
  onClose: () => void;
  onLogout: () => void;
  businesses: Array<{ id: string; name: string; favicon_url?: string | null }>;
}

export const MobileMenuPageContent = ({
  onClose,
  onLogout,
  businesses,
}: MobileMenuPageContentProps) => {
  const { user, isEmployee } = useAuth();
  const { currentPlan } = useSubscription();
  const showPortfolio = canOpenPortfolio(currentPlan?.name, businesses.length);
  const { theme, setTheme } = useTheme();
  const { openAccountSettings } = useAccountSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const userEmail = user?.email ?? '';
  const userName = user?.user_metadata?.full_name ?? userEmail.split('@')[0] ?? '';
  const initials = userName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSettings = () => {
    openAccountSettings();
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="text-lg font-bold">Menu</h2>
        <SheetClose asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-md h-auto md:h-auto w-auto md:w-auto p-1.5 md:p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="!h-5 !w-5" />
          </Button>
        </SheetClose>
      </div>

      {/* User Profile */}
      <div className="flex items-center gap-3 px-5 py-4">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{userName}</p>
          <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
        </div>
      </div>

      <Separator />

      {/* Navigation */}
      <div className="px-3 py-3">
        <p className="px-2 pb-2 text-2xs font-bold uppercase tracking-wider text-muted-foreground">
          Navigation
        </p>
        {!isEmployee && showPortfolio && (
          <NavItem
            icon={<Building2 className="h-4 w-4" />}
            label="Portfolio"
            active={isActive('/portfolio')}
            onClick={() => handleNav('/portfolio')}
          />
        )}
        <NavItem
          icon={<LayoutDashboard className="h-4 w-4" />}
          label="Dashboard"
          active={isActive('/dashboard')}
          onClick={() => handleNav('/dashboard')}
        />
        {!isEmployee && (
          <NavItem
            icon={<Users className="h-4 w-4" />}
            label="Team"
            active={isActive('/employee-dashboard')}
            onClick={() => handleNav('/employee-dashboard')}
          />
        )}
      </div>

      <Separator className="mx-3" />

      {/* Appearance */}
      <div className="px-3 py-3">
        <p className="px-2 pb-2 text-2xs font-bold uppercase tracking-wider text-muted-foreground">
          Appearance
        </p>
        <ButtonGroup className="w-full px-2">
          <Button
            variant="outline"
            size="default"
            className={cn(
              'flex-1 justify-center',
              theme === 'light' && 'bg-primary text-primary-foreground hover:bg-primary/90'
            )}
            onClick={() => setTheme('light')}
          >
            <Sun className="h-4 w-4" />
            <span className="ml-2">Light</span>
          </Button>
          <Button
            variant="outline"
            size="default"
            className={cn(
              'flex-1 justify-center',
              theme === 'dark' && 'bg-primary text-primary-foreground hover:bg-primary/90'
            )}
            onClick={() => setTheme('dark')}
          >
            <Moon className="h-4 w-4" />
            <span className="ml-2">Dark</span>
          </Button>
          <Button
            variant="outline"
            size="default"
            className={cn(
              'flex-1 justify-center',
              theme === 'system' && 'bg-primary text-primary-foreground hover:bg-primary/90'
            )}
            onClick={() => setTheme('system')}
          >
            <Monitor className="h-4 w-4" />
            <span className="ml-2">System</span>
          </Button>
        </ButtonGroup>
      </div>

      <Separator className="mx-3" />

      {/* Account */}
      <div className="px-3 py-3">
        <p className="px-2 pb-2 text-2xs font-bold uppercase tracking-wider text-muted-foreground">
          Account
        </p>
        <NavItem
          icon={<Settings className="h-4 w-4" />}
          label="Settings"
          onClick={handleSettings}
        />
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="flex items-center justify-start gap-3 w-full h-auto md:h-auto rounded-md px-2 md:px-2 py-2.5 md:py-2.5 text-sm md:text-sm font-medium text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Log Out
        </Button>
      </div>
    </div>
  );
};

function NavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between gap-0 w-full h-auto md:h-auto rounded-md px-2 md:px-2 py-2.5 md:py-2.5 text-sm md:text-sm font-medium whitespace-normal transition-colors',
        active
          ? 'bg-primary/10 text-foreground hover:bg-primary/10 hover:text-foreground'
          : 'text-foreground hover:bg-muted'
      )}
    >
      <span className="flex items-center gap-3">
        {icon}
        <span className={cn(active && 'text-primary')}>{label}</span>
      </span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Button>
  );
}
